import { motion, AnimatePresence } from "framer-motion";
import { X } from "lucide-react";
import { useState } from "react";

export default function TicketModal({ open, onClose, onSubmit }) {
  const [form, setForm] = useState({
    fullName: "",
    email: "",
    subject: "",
    category: "General",
    priority: "Normal",
    message: "",
    attachment: null,
  });

  const handleChange = (field, value) => setForm((p) => ({ ...p, [field]: value }));
  const handleFile = (e) => handleChange("attachment", e.target.files?.[0] || null);
  const handleSubmit = (e) => {
    e.preventDefault();
    onSubmit?.(form);
    onClose?.();
  };

  return (
    <AnimatePresence>
      {open && (
        <motion.div
          className="fixed inset-0 z-[100]"
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
        >
          {/* Backdrop */}
          <div className="absolute inset-0 backdrop-blur-sm bg-black/50" onClick={onClose} />

          {/* Modal card */}
          <motion.div
            role="dialog"
            aria-modal="true"
            initial={{ opacity: 0, y: 30, scale: 0.98 }}
            animate={{ opacity: 1, y: 0, scale: 1 }}
            exit={{ opacity: 0, y: 20, scale: 0.98 }}
            transition={{ duration: 0.25 }}
            className="relative mx-auto mt-16 w-[92%] max-w-2xl overflow-hidden rounded-2xl bg-white shadow-2xl"
          >
            {/* Decorative gradient header */}
            <div className="relative w-full h-20 bg-gradient-to-r from-emerald-500 to-emerald-400">
              <div className="absolute -top-10 -left-10 w-40 h-40 rounded-full blur-2xl bg-white/15" />
              <div className="absolute -right-12 -bottom-12 w-40 h-40 rounded-full blur-2xl bg-white/10" />
              <button
                aria-label="Close"
                onClick={onClose}
                className="inline-flex absolute top-3 right-3 justify-center items-center w-8 h-8 text-white rounded-full bg-black/10 hover:bg-black/20"
              >
                <X size={16} />
              </button>
              <h3 className="absolute bottom-3 left-6 text-lg font-semibold text-white">Submit a Support Ticket</h3>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} className="grid grid-cols-1 gap-4 p-5 sm:grid-cols-2">
              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700">Full Name</label>
                <input
                  required
                  value={form.fullName}
                  onChange={(e) => handleChange("fullName", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  placeholder="Jane Doe"
                />
              </div>
              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700">Email</label>
                <input
                  required
                  type="email"
                  value={form.email}
                  onChange={(e) => handleChange("email", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  placeholder="you@example.com"
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block mb-1 text-xs font-medium text-gray-700">Subject</label>
                <input
                  required
                  value={form.subject}
                  onChange={(e) => handleChange("subject", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  placeholder="Brief description"
                />
              </div>

              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700">Category</label>
                <select
                  value={form.category}
                  onChange={(e) => handleChange("category", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                >
                  <option>General</option>
                  <option>Payments</option>
                  <option>Claims</option>
                  <option>Technical</option>
                </select>
              </div>
              <div>
                <label className="block mb-1 text-xs font-medium text-gray-700">Priority</label>
                <select
                  value={form.priority}
                  onChange={(e) => handleChange("priority", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                >
                  <option>Low</option>
                  <option>Normal</option>
                  <option>High</option>
                  <option>Urgent</option>
                </select>
              </div>

              <div className="sm:col-span-2">
                <label className="block mb-1 text-xs font-medium text-gray-700">Message</label>
                <textarea
                  required
                  rows={4}
                  value={form.message}
                  onChange={(e) => handleChange("message", e.target.value)}
                  className="px-3 py-2 w-full text-sm rounded-lg border border-gray-200 focus:outline-none focus:ring-2 focus:ring-emerald-500/30"
                  placeholder="Describe your issue in detail..."
                />
              </div>

              <div className="sm:col-span-2">
                <label className="block mb-1 text-xs font-medium text-gray-700">Attachment (optional)</label>
                <input type="file" onChange={handleFile} className="block w-full text-xs text-gray-600 file:mr-3 file:rounded-md file:border file:border-gray-200 file:bg-white file:px-3 file:py-2 file:text-xs file:font-medium hover:file:bg-gray-50" />
              </div>

              <div className="flex gap-3 justify-end mt-2 sm:col-span-2">
                <button type="button" onClick={onClose} className="px-4 h-10 text-sm font-medium text-gray-700 bg-gray-100 rounded-lg hover:bg-gray-200">Cancel</button>
                <button type="submit" className="px-5 h-10 text-sm font-semibold text-white bg-emerald-600 rounded-lg hover:bg-emerald-500">Submit Ticket</button>
              </div>
            </form>
          </motion.div>
        </motion.div>
      )}
    </AnimatePresence>
  );
}


